import React, { useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import cx from "classnames";
import { isEmpty } from "lodash";
import Dropdown from "antd/lib/dropdown";
import Modal from "antd/lib/modal";
import Menu from "antd/lib/menu";
import recordEvent from "@/services/recordEvent";
import { Moment } from "@/components/proptypes";
import PlainButton from "@/components/plain-button/PlainButton";
import { useTheme } from "@/components/ThemeProvider";
import "./Widget.less";

function WidgetDropdownButton({ extraOptions, showDeleteOption, onDelete }) {
  const { isDarkMode } = useTheme();

  const WidgetMenu = (
    <Menu data-test="WidgetDropdownButtonMenu" className={isDarkMode ? 'dark-mode' : ''}>
      {extraOptions}
      {showDeleteOption && extraOptions && <Menu.Divider />}
      {showDeleteOption && <Menu.Item onClick={onDelete}>Remove from Dashboard</Menu.Item>}
    </Menu>
  );

  return (
    <div className="widget-menu-regular">
      <Dropdown overlay={WidgetMenu} placement="bottomRight" trigger={["click"]}>
        <PlainButton className="action p-l-15 p-r-15" data-test="WidgetDropdownButton" aria-label="More options">
          <i className="zmdi zmdi-more-vert" aria-hidden="true" />
        </PlainButton>
      </Dropdown>
    </div>
  );
}

WidgetDropdownButton.propTypes = {
  extraOptions: PropTypes.node,
  showDeleteOption: PropTypes.bool,
  onDelete: PropTypes.func,
};

WidgetDropdownButton.defaultProps = {
  extraOptions: null,
  showDeleteOption: false,
  onDelete: () => {},
};

function WidgetDeleteButton({ onClick }) {
  return (
    <div className="widget-menu-remove">
      <PlainButton
        className="action"
        title="Remove From Dashboard"
        onClick={onClick}
        data-test="WidgetDeleteButton"
        aria-label="Close">
        <i className="zmdi zmdi-close" aria-hidden="true" />
      </PlainButton>
    </div>
  );
}

WidgetDeleteButton.propTypes = { onClick: PropTypes.func };
WidgetDeleteButton.defaultProps = { onClick: () => {} };

function Widget({
  widget,
  className,
  children,
  header,
  footer,
  canEdit,
  isPublic,
  refreshStartedAt,
  menuOptions,
  tileProps,
  onDelete,
}) {
  const { isDarkMode } = useTheme();

  useEffect(() => {
    recordEvent("view", "widget", widget.id);
  }, [widget.id]);

  const deleteWidget = useCallback(() => {
    Modal.confirm({
      title: "Delete Widget",
      content: "Are you sure you want to remove this widget from the dashboard?",
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => widget.delete().then(onDelete),
      maskClosable: true,
      autoFocusButton: null,
      className: isDarkMode ? "dark-mode" : "",
    });
  }, [widget, onDelete, isDarkMode]);

  const showDropdownButton = !isPublic && (canEdit || !isEmpty(menuOptions));

  return (
    <div className={`widget-wrapper ${isDarkMode ? "dark-mode" : ""}`}>
      <div className={cx("tile body-container", className, { "dark-mode": isDarkMode })} {...tileProps}>
        <div className="widget-actions">
          {showDropdownButton && (
            <WidgetDropdownButton
              extraOptions={menuOptions}
              showDeleteOption={canEdit}
              onDelete={deleteWidget}
            />
          )}
          {canEdit && <WidgetDeleteButton onClick={deleteWidget} />}
        </div>
        <div className={`body-row widget-header ${isDarkMode ? "dark-mode" : ""}`}>{header}</div>
        {children}
        {footer && <div className="body-row tile__bottom-control">{footer}</div>}
      </div>
    </div>
  );
}

Widget.propTypes = {
  widget: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  className: PropTypes.string,
  children: PropTypes.node,
  header: PropTypes.node,
  footer: PropTypes.node,
  canEdit: PropTypes.bool,
  isPublic: PropTypes.bool,
  refreshStartedAt: Moment,
  menuOptions: PropTypes.node,
  tileProps: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  onDelete: PropTypes.func,
};

Widget.defaultProps = {
  className: "",
  children: null,
  header: null,
  footer: null,
  canEdit: false,
  isPublic: false,
  refreshStartedAt: null,
  menuOptions: null,
  tileProps: {},
  onDelete: () => {},
};

export default Widget;
